import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { CHAT_SEARCH_MAX_QUERY_LENGTH, chatSearchMinQueryLength } from '@fias/platform-resources';
import { CHAT_SEARCH_DEFAULT_MATCH_MODE } from '../hooks/useChatMessageSearch';
import { useChatGlobalSearch } from '../hooks/useChatGlobalSearch';
import { NO_MATCH_HINT, searchTermsForHighlight } from './message-search-utils';
import { MessageSearchResultRow } from './MessageSearchResultRow';
import { SenderFilter } from './SenderFilter';
import { SearchMatchModeToggle } from './SearchMatchModeToggle';
import type { ChatMessageSearchHitResponse, ChatSearchMatchMode } from '@/lib/api';

interface AllChatsSearchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Label for the conversation a hit came from (e.g. a DM partner's name or
   * "#general"). Supplied by the page, which already holds the channel list.
   */
  resolveChannelLabel: (hit: ChatMessageSearchHitResponse) => string;
  /** Open the hit's conversation and jump to the message. */
  onSelectResult: (hit: ChatMessageSearchHitResponse) => void;
}

/**
 * "Search all chats": text and/or sender search across every conversation
 * the user is in. The per-channel {@link ChannelSearchPanel}'s counterpart —
 * same filters, same result rendering — with each hit labelled by its
 * conversation. Mount it only while open so each visit starts clean.
 */
export function AllChatsSearchPanel({
  open,
  onOpenChange,
  resolveChannelLabel,
  onSelectResult,
}: AllChatsSearchPanelProps) {
  const [rawQuery, setRawQuery] = useState('');
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  const [matchMode, setMatchMode] = useState<ChatSearchMatchMode>(CHAT_SEARCH_DEFAULT_MATCH_MODE);
  const query = useDebouncedValue(rawQuery, 250);

  const {
    results,
    total,
    totalCapped,
    isLoading,
    isError,
    isTooBroad,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChatGlobalSearch(query, senderFilter, open, matchMode);

  const trimmed = query.trim();
  const minLength = chatSearchMinQueryLength(matchMode);
  const hasTerms = trimmed.length >= minLength;
  const isQueryable = hasTerms || !!senderFilter;
  const isDebouncing = rawQuery.trim() !== trimmed;
  const terms = useMemo(
    () => searchTermsForHighlight(trimmed, hasTerms, matchMode),
    [trimmed, hasTerms, matchMode],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-primary flex-shrink-0" />
          <DialogTitle className="text-base font-semibold">Search all chats</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Search every conversation you are in by message text, by sender, or both.
        </DialogDescription>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              maxLength={CHAT_SEARCH_MAX_QUERY_LENGTH}
              autoFocus
              placeholder="Search all chats"
              aria-label="Search messages in all chats"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            {rawQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">From</span>
            {/* No roster spans every conversation, so there are no suggestions —
                the filter takes a typed handle. */}
            <SenderFilter members={[]} value={senderFilter} onChange={setSenderFilter} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Match</span>
            <SearchMatchModeToggle value={matchMode} onChange={setMatchMode} />
          </div>
          {isQueryable && !isLoading && !isError && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {total}
              {totalCapped ? '+' : ''} {total === 1 && !totalCapped ? 'match' : 'matches'}
            </p>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border">
          {!isQueryable ? (
            <div className="text-center py-10 px-6">
              <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Type at least {minLength} characters to search all your chats
              </p>
              {matchMode === 'contains' && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  For shorter words, switch to “Whole word”.
                </p>
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">
                Or enter a username under “From” to see everything they wrote.
              </p>
            </div>
          ) : isLoading || isDebouncing ? (
            <p className="text-sm text-muted-foreground text-center py-10">Searching…</p>
          ) : isTooBroad ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">That search matches too many messages</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Add more words, use a longer phrase, or pick a sender under “From”.
              </p>
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive text-center py-10">
              Couldn&apos;t run that search. Try again.
            </p>
          ) : results.length === 0 ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">No messages found</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {hasTerms
                  ? NO_MATCH_HINT[matchMode]
                  : 'They haven’t posted in any of your conversations.'}
              </p>
            </div>
          ) : (
            <ul className="py-1">
              {results.map((hit) => (
                <li key={hit.messageId}>
                  <DialogClose asChild>
                    <button
                      type="button"
                      onClick={() => onSelectResult(hit)}
                      className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                    >
                      <MessageSearchResultRow
                        senderName={hit.senderName || hit.senderId}
                        content={hit.content}
                        createdAt={hit.createdAt}
                        terms={terms}
                        subtitle={resolveChannelLabel(hit)}
                      />
                    </button>
                  </DialogClose>
                </li>
              ))}
            </ul>
          )}
          {hasNextPage && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
